import type { QuoteFormData } from '../types/quote';
import { readFileAsDataUrl } from '../utils/imageUtils';

const env = (import.meta.env.VITE_ENV || import.meta.env.MODE || '').toLowerCase();
const isProd = env === 'prd' || env === 'prod' || env === 'production';
const DEFAULT_ORDERS_SERVICE_URL = isProd
  ? 'https://orders.speedyssey.com'
  : 'https://orders.dev.speedyssey.com';

const ORDERS_SERVICE_BASE_URL =
  import.meta.env.VITE_ORDERS_SERVICE_URL || DEFAULT_ORDERS_SERVICE_URL;

export interface SubmitQuoteResponse {
  message: string;
  quoteRequest: {
    id: string;
    referenceNumber: string;
    status: string;
    createdAt: string;
    [key: string]: any;
  };
}

/**
 * Submit a quote request to the order-service backend
 */
export async function submitQuoteRequest(formData: QuoteFormData): Promise<SubmitQuoteResponse> {
  const payload = {
    fullName: formData.fullName.trim(),
    companyName: formData.companyName?.trim() || undefined,
    phoneNumber: formData.phoneNumber.trim(),
    email: formData.email.trim(),
    pickupAddress: formData.pickupAddress.trim(),
    pickupContactName: formData.pickupContactName.trim(),
    pickupContactPhone: formData.pickupContactPhone.trim(),
    pickupDetails: formData.pickupDetails,
    deliveryAddress: formData.deliveryAddress.trim(),
    receiverName: formData.receiverName.trim(),
    receiverPhoneNumber: formData.receiverPhoneNumber.trim(),
    deliveryDetails: formData.deliveryDetails,
    whatAreYouSending: formData.whatAreYouSending.trim(),
    productImages: await Promise.all(
      (formData.productImages || []).map(async (img) => {
        let dataUrl = img.dataUrl;
        if (!dataUrl && img.file) {
          try {
            dataUrl = await readFileAsDataUrl(img.file);
          } catch {
            // fallback
          }
        }
        return {
          id: img.id,
          name: img.name,
          size: img.size,
          dataUrl,
          previewUrl: dataUrl || img.previewUrl,
        };
      })
    ),
    specialDeliveryInstructions: formData.specialDeliveryInstructions?.trim() || undefined,
    preferredPickupDate: formData.preferredPickupDate.trim(),
    preferredPickupTime: formData.preferredPickupTime?.trim() || undefined,
    specialHandling: formData.specialHandling || [],
    otherHandlingDetails: formData.otherHandlingDetails?.trim() || undefined,
    additionalHandlingInstructions: formData.additionalHandlingInstructions?.trim() || undefined,
    customerConfirmation: formData.customerConfirmation,
    summaryPdfBase64: formData.summaryPdfBase64,
  };

  const response = await fetch(`${ORDERS_SERVICE_BASE_URL}/v1/quote-requests`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let errorMsg = `Failed to submit quote request (Status: ${response.status})`;
    try {
      const errorData = await response.json();
      errorMsg = errorData.error || errorData.message || errorMsg;
    } catch {
      // response wasn't JSON
    }
    throw new Error(errorMsg);
  }

  return response.json();
}
